import { difference, featureCollection, getCoords } from '@turf/turf';
import { getSector } from '@utils/map/sector.utils';
import { isSensorOrSubSensorConfigurationOfType } from '@utils/sensors/sensors.utils';
import { Feature } from 'geojson';

import { FeatureTypeEnum } from '@/types/map.types';
import { RadarConfiguration } from '@/types/sensor/configuration.types';
import { SubSensorTypeEnum } from '@/types/sensor/sensors';

function getCenter(radar: RadarConfiguration) {
  if (isSensorOrSubSensorConfigurationOfType(radar, SubSensorTypeEnum.SPEXER_RADAR)) {
    return radar.position;
  } else if (isSensorOrSubSensorConfigurationOfType(radar, SubSensorTypeEnum.ROBIN_MAX_RADAR)) {
    return radar.position;
  } else {
    return radar.sensorPosition;
  }
}

export const toRadarCoverageFeature = (radar: RadarConfiguration): Feature[] => {
  if (!radar.aperture || (radar.endDetectionRadius === 0 && radar.intermediateRadius === 0)) {
    return [];
  }

  const orientation = radar.orientation ?? 0;
  const bearing1 = orientation - radar.aperture / 2;
  const bearing2 = orientation + radar.aperture / 2;
  const center = getCenter(radar);
  const code = radar.code;

  const sectorMax = getSector(center, bearing1, bearing2, radar.endDetectionRadius);
  const sectorIntermediate = getSector(center, bearing1, bearing2, radar.intermediateRadius);
  const sectorBlind = getSector(center, bearing1, bearing2, radar.blindRadius);

  const safeSector = sectorIntermediate ?? sectorMax;
  if (safeSector === null) {
    return [];
  }
  const features: Feature[] = [];

  const firstSector = sectorBlind ? difference(featureCollection([safeSector, sectorBlind])) : safeSector;
  const secondSector =
    sectorMax && sectorIntermediate ? difference(featureCollection([sectorMax, sectorIntermediate])) : null;

  if (firstSector) {
    features.push({
      type: 'Feature',
      id: `${FeatureTypeEnum.RADAR_COVERAGE}-${code}_first_sector`,
      properties: {
        featureType: FeatureTypeEnum.RADAR_COVERAGE,
        id: `${code}_first_sector`,
        value: radar,
        color: radar.sensorColor,
        opacity: 0.4,
      },
      geometry: {
        type: 'Polygon',
        coordinates: getCoords(firstSector),
      },
    });
  }

  if (secondSector) {
    features.push({
      type: 'Feature',
      id: `${FeatureTypeEnum.RADAR_COVERAGE}-${code}_second_sector`,
      properties: {
        featureType: FeatureTypeEnum.RADAR_COVERAGE,
        id: `${code}_second_sector`,
        value: radar,
        color: radar.sensorColor,
        opacity: 0.2,
      },
      geometry: {
        type: 'Polygon',
        coordinates: getCoords(secondSector),
      },
    });
  }
  return features;
};
